<template>
  <div>
    <BaseSimpleLayout class="order-auto-map">
      <template #title>製品自動マッピング</template>
      <template v-if="loadingFlag" #button-area>
        <ScreenPermissionGate code="ORDER_AUTO_MAP-BTN_CREATE">
          <el-button type="success" size="large" @click="openCreateDialog">
            登録
          </el-button>
        </ScreenPermissionGate>
      </template>
      <template v-if="loadingFlag" #main>
        <el-table :data="tableData" border :max-height="tableHeight">
          <el-table-column
            prop="id"
            label="ID"
            width="100"
            align="center"
            sortable
          />
          <el-table-column
            prop="deviceType"
            label="MCCS ver"
            width="140"
            align="center"
          >
            <template #default="scope">
              {{ scope.row.deviceTypeName }}
            </template>
          </el-table-column>
          <el-table-column prop="productName" label="製品名" min-width="200">
            <template #default="scope">
              <a
                v-if="scope.row.productId != null && canOpenProductDetail"
                href="#"
                @click.prevent="openProductDetail(scope.row.productId)"
              >
                {{ scope.row.productName }} (ID: {{ scope.row.productId }})
              </a>
              <template v-else-if="scope.row.productId != null">
                {{ scope.row.productName }} (ID: {{ scope.row.productId }})
              </template>
              <template v-else>-</template>
            </template>
          </el-table-column>
          <el-table-column
            prop="isUseMasterPrice"
            label="価格マッピング"
            width="160"
            align="center"
          >
            <template #default="scope">
              {{ scope.row.isUseMasterPrice ? 'マスタ価格参照' : 'SF価格参照' }}
            </template>
          </el-table-column>
          <el-table-column prop="condition" label="条件" min-width="360">
            <template #default="scope">
              <div class="condition-summary">
                <span
                  v-for="(part, index) in getConditionSummaryParts(
                    scope.row.condition,
                    conditionDisplayContext
                  )"
                  :key="index"
                >
                  <template v-if="index > 0">&nbsp;</template>
                  <span v-if="part.combinator" class="condition-combinator">{{
                    part.text
                  }}</span>
                  <strong v-else-if="part.bold">{{ part.text }}</strong>
                  <template v-else>{{ part.text }}</template>
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            v-if="showActionColumn"
            label="操作"
            width="180"
            align="center"
          >
            <template #default="scope">
              <ScreenPermissionGate code="ORDER_AUTO_MAP-BTN_EDIT">
                <el-button
                  type="primary"
                  size="small"
                  @click="openEditDialog(scope.row)"
                >
                  編集
                </el-button>
              </ScreenPermissionGate>
              <ScreenPermissionGate code="ORDER_AUTO_MAP-BTN_DELETE">
                <el-button
                  type="danger"
                  size="small"
                  plain
                  @click="confirmDelete(scope.row)"
                >
                  削除
                </el-button>
              </ScreenPermissionGate>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </BaseSimpleLayout>

    <OrderAutoMapFormDialog
      :visible="formDialogVisible"
      :row="editingRow"
      :product-list="productList"
      @save="handleSave"
      @cancel="closeFormDialog"
    />

    <BaseConfirmDialog
      v-model="deleteDialogVisible"
      title="削除確認"
      message="この製品自動マッピングを削除しますか。"
      confirm-text="削除"
      cancel-text="キャンセル"
      confirm-type="danger"
      :loading="isDeleting"
      @confirm="handleDeleteConfirm"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import router from '@/router';
  import BaseSimpleLayout from '@/components/BaseSimpleLayout.vue';
  import BaseConfirmDialog from '@/components/BaseConfirmDialog.vue';
  import ScreenPermissionGate from '@/components/ScreenPermissionGate.vue';
  import OrderAutoMapFormDialog from './OrderAutoMapFormDialog.vue';
  import { ElLoading } from 'element-plus';
  import { useStore } from 'vuex';
  import { success, error } from '@/libs/notification';
  import { hasScreenCodePermission } from '@/libs/auth';
  import {
    getConditionSummaryParts,
    type ConditionDisplayContext
  } from '@/libs/orderAutoMap';
  import type {
    OrderAutoMapFormData,
    OrderAutoMapRow
  } from '@/types/master/orderAutoMap';
  import type { ProductsDataState } from '@/types/master/product';

  const store = useStore();
  const tableData = ref<OrderAutoMapRow[]>([]);
  const loadingFlag = ref(false);
  const tableHeight = ref(600);
  const formDialogVisible = ref(false);
  const editingRow = ref<OrderAutoMapRow | null>(null);
  const deleteDialogVisible = ref(false);
  const deletingRow = ref<OrderAutoMapRow | null>(null);
  const isDeleting = ref(false);

  const productList = computed<ProductsDataState[]>(
    () => store.state.productsMaster.listData
  );

  const conditionDisplayContext = computed<ConditionDisplayContext>(() => ({
    businessTypeLabels: store.state.businessTypesMaster.replaceData
  }));

  const showActionColumn = computed(
    () =>
      hasScreenCodePermission('ORDER_AUTO_MAP-BTN_EDIT') ||
      hasScreenCodePermission('ORDER_AUTO_MAP-BTN_DELETE')
  );

  const canOpenProductDetail = computed(() =>
    hasScreenCodePermission('PRODUCT', 'VIEW')
  );

  const loadData = async () => {
    await store.dispatch('orderAutoMap/fetch');
    tableData.value = [...store.state.orderAutoMap.listData].sort(
      (a: OrderAutoMapRow, b: OrderAutoMapRow) => a.id - b.id
    );
  };

  const initialize = async () => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      await Promise.all([
        loadData(),
        store.dispatch('businessTypesMaster/fetch')
      ]);
      tableHeight.value = window.innerHeight - 120;
      loadingFlag.value = true;
    } finally {
      loadingInstance.close();
    }
  };

  const openCreateDialog = () => {
    editingRow.value = null;
    formDialogVisible.value = true;
  };

  const openEditDialog = (row: OrderAutoMapRow) => {
    editingRow.value = { ...row };
    formDialogVisible.value = true;
  };

  const closeFormDialog = () => {
    formDialogVisible.value = false;
    editingRow.value = null;
  };

  const handleSave = async (formData: OrderAutoMapFormData) => {
    const loadingInstance = ElLoading.service({ fullscreen: true });
    try {
      if (formData.id) {
        await store.dispatch('orderAutoMap/update', {
          id: formData.id,
          deviceType: formData.deviceType,
          productId: formData.productId,
          isUseMasterPrice: formData.isUseMasterPrice ?? false,
          condition: formData.condition
        });
        success('Success', '製品自動マッピングが正常に更新されました');
      } else {
        await store.dispatch('orderAutoMap/create', {
          deviceType: formData.deviceType,
          productId: formData.productId,
          isUseMasterPrice: formData.isUseMasterPrice ?? false,
          condition: formData.condition
        });
        success('Success', '製品自動マッピングが正常に登録されました');
      }
      await loadData();
      closeFormDialog();
    } catch {
      error('400', '保存中にエラーが発生しました');
    } finally {
      loadingInstance.close();
    }
  };

  const openProductDetail = (id: number) => {
    if (!canOpenProductDetail.value) return;
    window.open(router.resolve(`/master/product/${id}`).href, '_blank');
  };

  watch(deleteDialogVisible, (visible) => {
    if (!visible) {
      deletingRow.value = null;
    }
  });

  const confirmDelete = (row: OrderAutoMapRow) => {
    deletingRow.value = row;
    deleteDialogVisible.value = true;
  };

  const handleDeleteConfirm = async () => {
    if (!deletingRow.value) return;

    isDeleting.value = true;
    try {
      await store.dispatch('orderAutoMap/delete', deletingRow.value.id);
      success('Success', '製品自動マッピングが正常に削除されました');
      deleteDialogVisible.value = false;
      deletingRow.value = null;
      await loadData();
    } catch {
      error('400', '削除中にエラーが発生しました');
    } finally {
      isDeleting.value = false;
    }
  };

  initialize();
</script>

<style lang="scss">
  .order-auto-map {
    .el-table {
      width: 100%;
      margin: 10px auto 0;

      thead th {
        background: #f5f7fa;
      }
    }

    .el-button {
      font-weight: 600;
    }

    .condition-summary {
      line-height: 1.6;
      white-space: normal;
      word-break: break-word;

      strong {
        font-weight: 700;
      }

      .condition-combinator {
        font-size: 1.05em;
        font-weight: 700;
        color: #000;
      }
    }
  }
</style>
